import { z } from 'zod';
export const scenarioSchema = z.enum(['novafit', 'greentech', 'cafe_maison']);
export type Scenario = z.infer<typeof scenarioSchema>;
const text = (max: number) => z.string().trim().max(max);
const cents = z.number().int().min(0).max(1000000);
export const periodSchema = z
  .object({ from: z.iso.date(), to: z.iso.date() })
  .refine((p) => p.from <= p.to && Date.parse(p.to) - Date.parse(p.from) <= 366 * 86400000);
export const variantSchema = z
  .object({
    id: z.uuid(),
    name: text(100).min(1),
    network: z.enum(['search', 'social', 'display']),
    audience: text(2000),
    headline: text(120),
    body: text(2000),
    callToAction: text(100),
    budgetCents: cents,
    cpmCents: z.number().int().min(1).max(1000000),
    clickRateBps: z.number().int().min(0).max(10000),
    conversionRateBps: z.number().int().min(0).max(10000),
    valueCents: cents,
    contributionCents: cents,
  })
  .refine((v) => v.contributionCents <= v.valueCents);
export type Variant = z.infer<typeof variantSchema>;
export const planSchema = z
  .object({
    title: text(140).min(3),
    scenario: scenarioSchema,
    objective: z.enum(['awareness', 'traffic', 'conversion']),
    context: text(3000),
    criteria: text(3000),
    hypothesisReason: text(3000),
    destination: z.union([
      z.literal(''),
      z
        .url()
        .max(2000)
        .refine((s) => s.startsWith('https://')),
    ]),
    period: periodSchema,
    budgetCents: cents,
    variants: z.array(variantSchema).min(1).max(4),
  })
  .refine(
    (p) =>
      new Set(p.variants.map((v) => v.id)).size === p.variants.length &&
      p.variants.reduce((sum, v) => sum + v.budgetCents, 0) <= p.budgetCents,
  );
export type Plan = z.infer<typeof planSchema>;
export type Campaign = { plan: Plan; archived: boolean; archiveReason?: string };
export function ready(p: Plan) {
  return (
    p.context.length >= 10 &&
    p.criteria.length >= 10 &&
    p.hypothesisReason.length >= 10 &&
    p.variants.every(
      (v) =>
        v.audience.length >= 10 &&
        v.headline.length >= 3 &&
        v.body.length >= 10 &&
        v.callToAction.length >= 2 &&
        v.budgetCents > 0,
    )
  );
}
export function simulate(v: Variant) {
  const impressions = Math.floor((v.budgetCents * 1000) / v.cpmCents);
  const clicks = Math.floor((impressions * v.clickRateBps) / 10000);
  const conversions = Math.floor((clicks * v.conversionRateBps) / 10000);
  return {
    variantId: v.id,
    spendCents: v.budgetCents,
    impressions,
    clicks,
    conversions,
    revenueCents: conversions * v.valueCents,
    contributionCents: conversions * v.contributionCents - v.budgetCents,
  };
}
export type Result = ReturnType<typeof simulate>;
export const ratio = (n: number, d: number) => (d > 0 ? n / d : null);
export type Row<T> = { id: string; version: string; data: T };
export type Cursor = { id: string; createdAt: string } | undefined;
export type Page<T> = {
  rows: Row<T>[];
  total: number;
  hasMore: boolean;
  nextCursor: Exclude<Cursor, undefined> | null;
};
export const resultSchema = z.object({
  variantId: z.uuid(),
  spendCents: cents,
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  conversions: z.number().int().nonnegative(),
  revenueCents: z.number().int().nonnegative(),
  contributionCents: z.number().int(),
});
export const runSchema = z.object({
  runId: z.uuid(),
  campaignId: z.uuid(),
  campaignVersion: z.string().min(1).max(160),
  at: z.iso.datetime(),
  provenance: z.literal('scenario'),
  engineVersion: z.literal('cpm-1'),
  plan: planSchema,
  results: z.array(resultSchema).min(1).max(4),
});
export type Run = z.infer<typeof runSchema>;
export const explanationSchema = z.object({
  title: text(140).min(3),
  diagnosis: text(5000).min(10),
  decision: text(5000).min(10),
  limitations: text(3000).min(10),
});
export type Explanation = z.infer<typeof explanationSchema>;
export type Report = { at: string; explanation: Explanation; runs: Run[] };
export type History = {
  campaignId: string;
  at: string;
  action: 'saved' | 'simulated' | 'archived' | 'reopened';
  snapshot: Campaign;
  reason: string;
  runId?: string;
};
export const requestSchema = z.object({ requestId: z.uuid() });
export const versionSchema = requestSchema.extend({
  id: z.uuid(),
  version: z.string().min(1).max(160),
});
export const saveSchema = requestSchema
  .extend({
    id: z.uuid().optional(),
    version: z.string().min(1).max(160).optional(),
    plan: planSchema,
  })
  .refine((v) => !!v.id === !!v.version);
export const cursorSchema = z
  .object({ id: z.uuid(), createdAt: z.iso.datetime({ offset: true }) })
  .optional();
export const compareSchema = requestSchema.extend({
  runIds: z
    .array(z.uuid())
    .min(2)
    .max(4)
    .refine((ids) => new Set(ids).size === ids.length),
  explanation: explanationSchema,
});
export function parisDay(instant: number) {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}
